/**
 * Business Scenario Define-and-Validate flow helpers (ROAR-4840, plan D3/D4).
 *
 * Small, pure helpers around the guardrail loop's payload shape and attempt-state bookkeeping, mirroring
 * `Chat/utils/idor.js`, so `DefineScenarioSection` stays declarative.
 */

import {
  SCENARIO_MIN_CHARS,
  SCENARIO_MAX_ATTEMPTS,
  SCENARIO_FEATURE_NAME_MAX_CHARS,
  SCENARIO_STEP_MAX_CHARS,
  sanitizeLlmText,
} from '../../../../../utils/constants/genpt';

/** Trimmed length is what counts toward the floor — whitespace-only text is not a scenario (plan §5). */
export function scenarioTrimmedLength(description) {
  return (description || '').trim().length;
}

/**
 * Flatten the Test Setup `savedCredential` form value to the scalar id the validate call expects, or
 * `null`. The form holds it as an object `{ value, id, label, name }` with `value: 'add-new'` as the
 * "type new credentials" sentinel; api-backend expects a bare id (it `parseInt`s it), so passing the
 * object through makes the saved-credential lookup fail and the scenario false-blocks. Mirrors the
 * start-test flattening (`savedCredential?.value || savedCredential?.id`).
 */
export function savedCredentialIdForValidate(savedCredential) {
  if (!savedCredential) return null;
  // A bare scalar (already an id) is passed through as-is.
  if (typeof savedCredential !== 'object') {
    return savedCredential === 'add-new' ? null : savedCredential;
  }
  // 'add-new' means the user is typing fresh credentials — not a saved credential to look up.
  if (savedCredential.value === 'add-new') return null;
  return savedCredential.value || savedCredential.id || null;
}

/** True once the description clears the 50-char floor. 50 is a floor, not a cap (plan §5) — there is
 * no corresponding "too long" check here; `SCENARIO_MAX_CHARS` is enforced by the textarea's own
 * `maxLength` instead. */
export function isScenarioDescriptionValid(description) {
  return scenarioTrimmedLength(description) >= SCENARIO_MIN_CHARS;
}

/**
 * Build the `POST /genpt/test/steps` guardrail payload (plan D3). `scenarioId` is included only when
 * re-validating an existing saved scenario (AC11 Edit) so api-backend updates that record in place
 * (TDD ADR-9) instead of creating a new one.
 *
 * Credentials are forwarded so api-backend can resolve credential-presence booleans for the guardrail
 * (so authenticated / MFA scenarios are not falsely blocked). `username`/`password`/`mfaSecretKey` are
 * the typed creds; `savedCredential` is the id when the user picked a saved credential instead. All are
 * omitted when empty, so a scenario with no creds falls back to the guardrail's strict behavior.
 */
export function scenarioValidatePayload({
  nonce,
  chatId,
  description,
  scenarioId = null,
  username = '',
  password = '',
  mfaSecretKey = '',
  savedCredential = null,
  candidateConfirmation = false,
} = {}) {
  const trimmedUsername = (username || '').trim();
  const trimmedPassword = (password || '').trim();
  const trimmedMfa = (mfaSecretKey || '').trim();

  return {
    test_type: 'scenario',
    nonce,
    chat_id: chatId,
    scenario_description: (description || '').trim(),
    ...(scenarioId ? { scenario_id: scenarioId } : {}),
    ...(trimmedUsername ? { username: trimmedUsername } : {}),
    ...(trimmedPassword ? { password: trimmedPassword } : {}),
    ...(trimmedMfa ? { mfaSecretKey: trimmedMfa } : {}),
    ...(savedCredential ? { savedCredential } : {}),
    // ROAR-4937: set when re-validating the chosen sub-scenario on Confirm, so api-backend overwrites
    // metadata.scenario with the child (single-feature) description/steps without consuming an attempt.
    ...(candidateConfirmation ? { candidate_confirmation: true } : {}),
  };
}

/**
 * Next attempts-remaining count after one guardrail call.
 *
 * ROAR-4986: the server is the ONLY source of this number. api-backend echoes an authoritative
 * `attempts_remaining` on every verdict it issues — success, blocked, empty-name, split, exhausted,
 * even the retryable failures — because only a VALID scenario consumes an attempt, so a verdict the
 * client cannot classify must never be guessed at. Trust a finite count whenever one arrives;
 * otherwise (an older backend that omits it) hold the last known count rather than decrementing,
 * which is what used to burn attempts the server never spent. A transport failure/5xx never reaches
 * this function at all — the caller only invokes it from a resolved (not rejected) mutation.
 */
export function nextAttemptsRemaining(previousRemaining, response) {
  if (Number.isFinite(response?.attempts_remaining)) {
    return response.attempts_remaining;
  }
  return Number.isFinite(previousRemaining) ? previousRemaining : SCENARIO_MAX_ATTEMPTS;
}

/** True once every attempt has been used up (plan §5, AC7 "0 ATTEMPTS LEFT" / START OVER THE TEST). */
export function attemptsExhausted(attemptsRemaining) {
  return Number.isFinite(attemptsRemaining) && attemptsRemaining <= 0;
}

/** Render-safe text for one Previous-Attempts row — an entry may arrive as a bare string or an object. */
export function scenarioAttemptText(attempt) {
  if (typeof attempt === 'string') return attempt;
  if (attempt && typeof attempt === 'object') {
    return attempt.description || attempt.scenario_description || attempt.message || '';
  }
  return '';
}

/**
 * Fields to fold into `TestSetup`'s `configData` once a scenario is confirmed (AC7).
 * `saveMode` (AC11) is 'update' when editing a saved scenario in place, 'new' when saving an edited
 * scenario as a fresh record, or null for Add-New / plain Run-Again (no write-back decision needed).
 */
export function confirmedScenarioFields({
  scenarioId = null,
  saveMode = null,
  description,
  featureName,
  steps = [],
} = {}) {
  return {
    scenarioId: scenarioId || null,
    scenarioSaveMode: saveMode || null,
    scenarioDescription: (description || '').trim(),
    featureName,
    scenarioSteps: Array.isArray(steps) ? steps : [],
  };
}

/**
 * Map a saved-scenario row/detail (a `genpt_scenarios` record from `GET /genpt/scenarios` or
 * `GET /genpt/scenarios/:id`) into the `confirmedScenarioFields` shape (ROAR-4511, CI/CD Business
 * Scenario). The store uses `id`/`name`, the session/detail overlay uses `scenario_id`/`feature_name`
 * — accept either. `saveMode` is always null here: a CI/CD pipeline only *references* an existing
 * scenario, it never creates/updates one. Degrades gracefully (empty steps / feature name) so callers
 * can render even a partial detail payload.
 *
 * This is the ONLY path that maps already-persisted scenario text (session snapshots, saved
 * `genpt_scenarios` rows) into what `DefineScenarioSection`/`SavedScenarioPicker` render — unlike the
 * live guardrail response (`normalizeScenarioValidation`, siembagpt.js), it previously skipped D6
 * sanitization entirely. A non-string `steps[]` entry (older/malformed persisted data) would then
 * throw "Objects are not valid as a React child" when rendered as `<span>{step}</span>`, with no
 * error boundary anywhere to catch it — this is what broke "View Setup Details" for some sessions.
 * Sanitize the same way the live path does so both paths share one contract.
 */
export function scenarioFieldsFromSavedRow(row) {
  if (!row || typeof row !== 'object') return confirmedScenarioFields({});
  return confirmedScenarioFields({
    scenarioId: row.scenario_id ?? row.id ?? null,
    description: row.description ?? row.scenario_description ?? '',
    featureName: sanitizeLlmText(
      row.feature_name ?? row.name ?? '',
      SCENARIO_FEATURE_NAME_MAX_CHARS
    ),
    steps: Array.isArray(row.steps)
      ? row.steps.map((step) => sanitizeLlmText(step, SCENARIO_STEP_MAX_CHARS)).filter(Boolean)
      : [],
  });
}

/**
 * True once a validation response has split into a genuine AC6 sub-scenario picker (Figma frame
 * 06a; ROAR-4842). The Cross-Repo Contract bounds `candidates[]` to 2-4 entries — a single-entry
 * array is deliberately NOT a split: rendering the "More than 1 scenario detected" banner over one
 * candidate would contradict its own copy, so this is `>= 2`, not `>= 1`.
 */
export function hasScenarioCandidates(candidates) {
  return Array.isArray(candidates) && candidates.length >= 2;
}

const EMPTY_CANDIDATE = { feature_name: '', description: '' };

/**
 * Bounds-safe read of the candidate at `index` (the radio selection). Falls back to index 0 for an
 * out-of-range index and to an empty candidate for an empty/non-array list, so the picker can
 * always render something rather than throwing mid-render.
 */
export function selectedScenarioCandidate(candidates, index) {
  if (!Array.isArray(candidates) || candidates.length === 0) return EMPTY_CANDIDATE;
  return candidates[index] ?? candidates[0];
}

/**
 * Immutable rename of one candidate's `feature_name` (the AC6 pencil — name only, plan decision
 * 2026-09-09: descriptions stay read-only LLM text pending a guardrail re-check design). Returns
 * the input array unchanged for an out-of-range index rather than throwing.
 */
export function renameScenarioCandidate(candidates, index, name) {
  if (!Array.isArray(candidates) || index < 0 || index >= candidates.length) return candidates;
  return candidates.map((candidate, i) =>
    i === index ? { ...candidate, feature_name: name } : candidate
  );
}

/**
 * Normalize a scenario name for duplicate comparison (ROAR-4919): strip control
 * characters, collapse internal whitespace, trim, and lowercase so "Login  Flow",
 * "login flow " and "Login Flow" compare equal.
 *
 * This is a deliberate mirror of the backend's `normalizeScenarioName`
 * (api-backend-provapt genptScenarioService.js). Keep the two in sync. For
 * COMPARISON only — never render or persist the normalized form. Unlike
 * `sanitizeLlmText`, this lowercases and applies no length cap.
 */
export function normalizeScenarioName(name) {
  if (typeof name !== 'string') return '';
  return (
    name
      // eslint-disable-next-line no-control-regex
      .replace(/[\u0000-\u001F\u007F-\u009F]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase()
  );
}

/**
 * True if `name` duplicates an existing scenario's name within the loaded list
 * (ROAR-4919), ignoring the row identified by `excludeId` (so an in-place edit
 * doesn't collide with itself). Accepts rows keyed either `name`/`id` (store
 * rows) or `feature_name`/`scenario_id` (session overlay). Empty names never
 * collide.
 */
export function scenarioNameCollides(name, scenarios, excludeId = null) {
  const target = normalizeScenarioName(name);
  if (!target) return false;
  const exclude = excludeId == null ? null : String(excludeId);
  return (Array.isArray(scenarios) ? scenarios : []).some((s) => {
    const id = String(s?.id ?? s?.scenario_id ?? '');
    if (exclude != null && id === exclude) return false;
    return normalizeScenarioName(s?.name ?? s?.feature_name) === target;
  });
}

/**
 * Collapse scenarios that share a normalized name (ROAR-4919) so pre-existing
 * duplicate rows already in the DB don't render twice in the dropdown. Keeps the
 * first occurrence; the list arrives ordered `created_at DESC` (newest first =
 * greatest id), so the kept row is the most recent. Rows with a null/blank name
 * are never collapsed.
 */
export function dedupeScenarioOptions(scenarios) {
  if (!Array.isArray(scenarios)) return [];
  const seen = new Set();
  return scenarios.filter((s) => {
    const key = normalizeScenarioName(s?.name ?? s?.feature_name);
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
